import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignalWithTransform,
  Signal,
  ViewEncapsulation
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { Router } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { TabsDataModel } from './tabs-data.interface';
import { tabsData } from './tabs-data';
import { AppRoutes, AUTH_CLIENT_TOKEN, AuthClientProvider, USER_DATA_TOKEN, UserDataProvider, UserModel } from '@core';
import { map, Observable, race, Subscription, switchMap, take, timer } from 'rxjs';
import { UnreadMessagesQueryHandler } from 'projects/messages/src/lib/application/handlers';

@Component({
  selector: 'lib-bottom-navbar',
  templateUrl: './bottom-navbar.component.html',
  styleUrls: ['./bottom-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [IonicModule, NgClass, AsyncPipe]
})
export class BottomNavbarComponent {
  private readonly router: Router = inject(Router);
  private readonly unreadMessagesQueryHandler: UnreadMessagesQueryHandler = inject(UnreadMessagesQueryHandler);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly authClientProvider: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);

  readonly fixedPosition: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });

  readonly isUnreadMessage$: Observable<boolean> = this.unreadMessagesQueryHandler.isUnreadMessage();

  readonly userId$: Observable<string> = this.userDataProvider.userData$.pipe(
    map((userData: UserModel) => userData.userId)
  );
  readonly isAuthed$: Observable<boolean> = this.authClientProvider.isAuthed$;

  readonly currentUserId: Signal<string | undefined> = toSignal(this.userId$);

  readonly tabsData: Signal<TabsDataModel[]> = computed<TabsDataModel[]>(() => tabsData);

  isActive(url: string): boolean {
    if (url === AppRoutes.PROFILE) {
      const userId: string | undefined = this.currentUserId();
      return !!userId && this.router.url.includes(`${AppRoutes.PROFILE}/${userId}`);
    }

    return this.router.url.includes(url);
  }

  redirect(route: string): Subscription | Promise<boolean> {
    if (route === AppRoutes.PROFILE)
      return race([this.userId$, timer(200).pipe(map(() => null))])
        .pipe(
          take(1),
          switchMap((userId: string | null) => {
            if (userId) return this.router.navigate([`/${AppRoutes.PROFILE}/${userId}`]);

            return this.router.navigate([AppRoutes.AUTH]);
          })
        )
        .subscribe();

    return this.router.navigate([`/${route}`]);
  }
}
